<template>
  <div class="header col-12">
    <h1>{{headerText}}</h1>
    <img @click="$router.push('/')" :src="iconSrc" alt="Icon">
    <div class="user-and-hat col-12">
      <p class="col-6 col-md-2" @click.stop="logout">{{$store.state.userEmail}}</p>
      <p class="col-6 col-md-2" @click.stop="$router.push('/meal-hats')">{{hatTitle}}</p>
    </div>
    <span class="build-stamp" role="button" title="Tap to reload" @click="reloadApp">{{ refreshing ? 'reloading…' : buildStamp }}</span>
  </div>
</template>

<script>
import { buildStamp, forceRefresh } from '../utils/buildStamp.js';
// Was `require('@/assets/icon.png')` inline in the template — a webpack-only
// spelling that Vite/Rollup cannot honour (there is no `require` in an ES
// module). A static import does exactly what require() did: the bundler
// rewrites it to the hashed `img/icon.<hash>.png` URL it emits.
import iconSrc from '@/assets/icon.png';

export default {
  props: {
    headerText: {
      type: String,
      required: true
    }
  },
  data () {
    return { iconSrc, refreshing: false };
  },
  computed: {
    // The house build stamp — "v1.14.0 · built Aug 22, 8:15 AM". Was the bare
    // version number; the version alone can't tell you whether the app in
    // front of you picked up the deploy you just did.
    buildStamp () {
      return buildStamp();
    },
    hatTitle () {
      if (this.$store.getters.primaryDatabaseTopKey === this.$store.state.databaseTopKey) {
        return "your default hat";
      } else {
        return this.$store.state.databaseTopKey;
      }
    }
  },
  methods: {
    // The stamp is the reload button: an installed PWA has no other one.
    reloadApp () {
      this.refreshing = true;
      forceRefresh();
    },
    logout () {
      this.$store.dispatch('logout');
      this.$router.push('/login');
    }
  },
};
</script>

<style lang="scss">
  .header {
    background: #274C77;
    color: white;
    cursor: pointer;
    /* iOS 26+ paints a Liquid Glass blur band over the top edge of an
       installed web app, regardless of the 'black' status-bar style. The
       title and icon used to sit ~8px from pixel 0, i.e. inside that band.
       The header grows by the band clearance, and because h1/img are absolutely
       positioned they resolve against the PADDING box — padding alone would
       not move them, so each carries the clearance too. The +45/+8/+9 keep
       the original relationship between title, icon and the room reserved
       below them; only the starting point moved. */
    padding: calc(var(--band-top) + 45px) 1rem 45px;
    position: relative;

    h1 {
      font-size: 2.5rem;
      font-family: "Playfair Display", serif;
      margin: 0;
      text-align: center;
      position: absolute;
      top: calc(var(--band-top) + 8px);
      left: 16px;
    }

    img {
      position: absolute;
      top: calc(var(--band-top) + 9px);
      right: 16px;
      height: 48px;
    }

    .user-and-hat {
      display: flex;
      justify-content: space-between;
      position: absolute;
      bottom: 6px;
      left: 0;

      p {
        font-size: 0.75rem;
        margin: 0;
        padding: 0.25rem;

        &:first-child {
          padding-left: 1rem;
          text-align: left;
        }

        &:last-child {
          padding-right: 1rem;
          text-align: right;
          text-decoration: underline;
        }
      }
    }

    // The house build stamp: present, readable, never competing for
    // attention. Same corner the bare version number used to sit in, but it
    // is a longer string now — line-height 1 and top 1px keep its 8px box
    // clear of the icon below it (top: 9px), and nowrap keeps it on one line
    // at phone width rather than wrapping under the icon.
    .build-stamp {
      font-family: "Roboto Condensed", sans-serif;
      font-size: 0.5rem;
      font-variant-numeric: tabular-nums;
      line-height: 1;
      opacity: 0.85;
      position: absolute;
      right: 3px;
      top: 1px;
      white-space: nowrap;
    }
  }
</style>